import type { IBooking } from '@cnt-workspace/models';
import { parseBookingDate } from '../shared/iso-date.util';
import { roundMoney } from './reservation-real-mapper';

/**
 * Client mirrors of the server's change/cancel rules, so the UI only offers
 * what the API will accept and previews the refund it will actually issue.
 * Keep in step with:
 *  - `Reservation.cancellable()` (objects/src/classes/reservation.ts) — gates `PUT /reservation/:id/change`
 *  - `ReservationService.cancelIfPossible` + `ReservationStatusUpdateService.cancel` (server) — refund amount/window
 */

export type CancellationPolicy = 'easy' | 'moderate' | 'strict' | 'exclusive';

const DAY_MS = 86_400_000;

/** Listings store either the key (`strict`) or the enum label (`Strict`). */
export function normalizePolicy(policy: string | undefined): CancellationPolicy {
  const p = (policy ?? '').toLowerCase();
  if (p.startsWith('moderate')) return 'moderate';
  if (p.startsWith('strict')) return 'strict';
  if (p.startsWith('exclusive')) return 'exclusive';
  return 'easy';
}

export const POLICY_LABEL: Record<CancellationPolicy, string> = {
  easy: "Easy Goin'",
  moderate: 'Moderate',
  strict: 'Strict',
  exclusive: 'Exclusive',
};

function checkIn(booking: IBooking): Date | null {
  return parseBookingDate(booking.dates.start);
}

/** Days before check-in that changes stay open (`Reservation.cancellable()`). */
function changeDaysInAdvance(policy: CancellationPolicy): number | null {
  switch (policy) {
    case 'exclusive': return null;
    case 'strict': return 7;
    case 'moderate': return 3;
    default: return 1;
  }
}

/** Last moment a guest can change the booking, or null if it never could. */
export function modifyDeadline(booking: IBooking): Date | null {
  const days = changeDaysInAdvance(normalizePolicy(booking.cancellationPolicy));
  const start = checkIn(booking);
  if (days === null || !start) return null;
  return new Date(start.getTime() - days * DAY_MS);
}

/**
 * Can the guest change dates/add-ons right now?
 *
 * Real bookings follow the server: only an accepted/paid booking (not a fresh
 * request, not one with a change already waiting on the host), not on an
 * Exclusive listing, and before the policy's change deadline. Mock bookings
 * keep the old rule — any live booking before check-in.
 */
export function canModifyBooking(booking: IBooking, now: Date = new Date()): boolean {
  if (booking.status === 'cancelled' || booking.status === 'declined') return false;
  const start = checkIn(booking);
  if (!start || start.getTime() <= now.getTime()) return false;
  if (!booking.realReservationId) return true;
  if (booking.status === 'pending') return false;
  const deadline = modifyDeadline(booking);
  return !!deadline && now.getTime() < deadline.getTime();
}

export interface CancelRefundPreview {
  /** `request`: a new request not yet accepted. `refund`: full refund. `partial`: Strict's 50%. `none`: no refund. */
  kind: 'request' | 'refund' | 'partial' | 'none';
  /** Refund amount, paid as CurbNTurf Cash. 0 for `none`/`request`. */
  amount: number;
  policy: CancellationPolicy;
  /** Last moment a cancel still earns the refund; null when there is none. */
  refundDeadline: Date | null;
}

/** What cancelling right now gets the guest back. */
export function cancelRefundPreview(booking: IBooking, now: Date = new Date()): CancelRefundPreview {
  const policy = normalizePolicy(booking.cancellationPolicy);

  if (booking.status === 'pending' && !booking.changeRequested) {
    return { kind: 'request', amount: 0, policy, refundDeadline: null };
  }

  if (policy === 'exclusive') {
    return { kind: 'none', amount: 0, policy, refundDeadline: null };
  }

  // Server refunds only when check-in is more than 7 (Strict) / 3 (others) days out.
  const windowDays = policy === 'strict' ? 7 : 3;
  const start = checkIn(booking);
  const refundDeadline = start ? new Date(start.getTime() - windowDays * DAY_MS) : null;
  if (!refundDeadline || now.getTime() >= refundDeadline.getTime()) {
    return { kind: 'none', amount: 0, policy, refundDeadline };
  }

  const rate = policy === 'strict' ? 0.5 : 1;
  return {
    kind: rate < 1 ? 'partial' : 'refund',
    amount: roundMoney(booking.total * rate),
    policy,
    refundDeadline,
  };
}

/** One-line refund terms for the trust strip / cancel dialog — server rules, not marketing copy. */
export function policySummary(policy: string | undefined): string {
  switch (normalizePolicy(policy)) {
    case 'exclusive': return 'Non-refundable once the host accepts.';
    case 'strict': return '50% back as CurbNTurf Cash up to 7 days before check-in.';
    default: return 'Full refund as CurbNTurf Cash up to 3 days before check-in.';
  }
}
